import { useEffect, useRef, useState } from 'react';
import { detectPhoneGrip, detectPenGrip, type PhoneGripResult, type PenGripResult } from '../engine/PhoneGripDetector';
import { HandTremorDetector, type TremorMetric } from '../engine/HandTremorDetector';

export interface HandGripSnapshot {
  center: { x: number; y: number } | null;
  phoneGrip: PhoneGripResult;
  penGrip: PenGripResult;
  /** Consecutive frames the phone grip has been sustained. */
  phoneSustainFrames: number;
  /** Consecutive frames the pen grip has been sustained. */
  penSustainFrames: number;
}

/** 5꼭지 양손 협응 패턴 — 양손 grip이 동시에 sustain되는 시나리오. */
export type HandCoordinationKind =
  | 'pen+phone'    // 한 손 pen + 다른 손 phone
  | 'phone+phone'  // 양손 phone (양손 폰)
  | 'pen+pen'      // 양손 pen (드문 시나리오)
  | null;

export interface HandState {
  /** At least one hand detected */
  detected: boolean;
  /** Number of hands detected (0-2) */
  handCount: number;
  /** Hand center in normalized [0,1] coordinates (primary hand) */
  center: { x: number; y: number } | null;
  /** Hand is moving (writing motion) — speed above threshold */
  isWriting: boolean;
  /** Raw landmarks for the primary hand (21 points) */
  landmarks: Array<{ x: number; y: number; z: number }> | null;
  /** Detection FPS */
  fps: number;
  /** Phone-grip pose detection (primary hand). */
  phoneGrip: PhoneGripResult;
  /** Pen-grip pose detection (primary hand). */
  penGrip: PenGripResult;
  /** Consecutive frames the phone grip has been sustained (for stability). */
  gripSustainFrames: number;
  /** Consecutive frames the pen grip has been sustained. */
  penGripSustainFrames: number;
  /** Sliding-window fingertip tremor metric (1s window). */
  tremor: TremorMetric;
  /** Secondary hand grip info (handCount ≥ 2일 때). null이면 없음. */
  secondary: HandGripSnapshot | null;
  /** 양손 협응 kind — 양손이 동시에 sustain ≥ 3 frames일 때만 set. */
  coordinationKind: HandCoordinationKind;
  /** 양손 협응이 sustain된 연속 프레임 수. */
  coordinationSustainFrames: number;
}

const INITIAL: HandState = {
  detected: false, handCount: 0, center: null,
  isWriting: false, landmarks: null, fps: 0,
  phoneGrip: { isGrip: false, center: null, confidence: 0 },
  penGrip:   { isGrip: false, center: null, confidence: 0 },
  gripSustainFrames: 0,
  penGripSustainFrames: 0,
  tremor: { amplitudePx: 0, freqHz: 0, count: 0, confidence: 0 },
  secondary: null,
  coordinationKind: null,
  coordinationSustainFrames: 0,
};

const MODEL_URL =
  'https://storage.googleapis.com/mediapipe-models/hand_landmarker/hand_landmarker/float16/1/hand_landmarker.task';
const DETECT_INTERVAL_MS = 66; // ~15 fps
const WRITING_SPEED_THRESH = 0.003; // normalized units per frame

/**
 * MediaPipe HandLandmarker hook.
 *
 * Detects hands in the webcam feed at ~15 fps. Returns hand landmarks,
 * whether a hand is detected, and whether the hand is actively moving
 * (proxy for "writing").
 *
 * Uses the same @mediapipe/tasks-vision package as FaceLandmarker — no
 * new dependencies. Model is ~4 MB float16, loaded lazily.
 */
/**
 * Face bbox in normalized [0,1] image coords. When provided and a hand
 * center falls inside the bbox, that hand is rejected as a likely
 * face-mesh false positive (e.g., MediaPipe hands sometimes fires on
 * detailed face features when minHandDetectionConfidence is borderline).
 */
export interface FaceBbox {
  minX: number; minY: number; maxX: number; maxY: number;
}

export function useHandLandmarker(
  videoElementId: string,
  overlayCanvasId: string,
  enabled: boolean,
  faceBboxRef?: React.MutableRefObject<FaceBbox | null>,
): HandState {
  const [state, setState] = useState<HandState>(INITIAL);
  const initRef = useRef(false);
  const animRef = useRef(0);

  useEffect(() => {
    if (!enabled) return;
    if (initRef.current) return;
    initRef.current = true;

    let disposed = false;

    const init = async () => {
      try {
        const vision = await import('@mediapipe/tasks-vision');
        const { HandLandmarker, FilesetResolver, DrawingUtils } = vision;

        const fileset = await FilesetResolver.forVisionTasks(
          'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.14/wasm',
        );

        const detector = await HandLandmarker.createFromOptions(fileset, {
          baseOptions: { modelAssetPath: MODEL_URL, delegate: 'GPU' },
          runningMode: 'VIDEO',
          numHands: 2,
          minHandDetectionConfidence: 0.6,
          minTrackingConfidence: 0.5,
        });

        if (disposed) return;

        let lastDetectTime = 0;
        let prevCenter: { x: number; y: number } | null = null;
        let frameCount = 0;
        let fpsTimer = performance.now();
        let drawUtils: any = null;
        let gripSustain = 0;
        let penGripSustain = 0;
        // Secondary hand sustain counters (양손 협응).
        let secPhoneSustain = 0;
        let secPenSustain = 0;
        let coordSustain = 0;
        const tremor = new HandTremorDetector();

        const detect = () => {
          if (disposed) return;
          animRef.current = requestAnimationFrame(detect);

          const now = performance.now();
          if (now - lastDetectTime < DETECT_INTERVAL_MS) return;

          const videoEl = document.getElementById(videoElementId) as HTMLVideoElement | null;
          if (!videoEl || !videoEl.videoWidth || videoEl.readyState < 2) return;
          lastDetectTime = now;

          try {
            const result = detector.detectForVideo(videoEl, now);
            const canvas = document.getElementById(overlayCanvasId) as HTMLCanvasElement | null;
            const ctx = canvas?.getContext('2d');

            // Ensure the canvas's intrinsic resolution matches the video.
            // When the hand canvas is separate from the face canvas, no one
            // else sets this, so landmarks would otherwise render into the
            // default 300×150 area.
            if (canvas && (canvas.width !== videoEl.videoWidth || canvas.height !== videoEl.videoHeight)) {
              canvas.width = videoEl.videoWidth;
              canvas.height = videoEl.videoHeight;
            }

            // Clear its own canvas each frame (safe when canvas is dedicated
            // to hand — a no-op visually when sharing face's canvas because
            // face already clears first).
            if (ctx && canvas) {
              ctx.clearRect(0, 0, canvas.width, canvas.height);
            }

            // Draw hand landmarks (mirrored to match CSS scaleX(-1) on video).
            if (ctx && canvas && result.landmarks && result.landmarks.length > 0) {
              if (!drawUtils) drawUtils = new DrawingUtils(ctx);

              ctx.save();
              ctx.translate(canvas.width, 0);
              ctx.scale(-1, 1);

              for (const hand of result.landmarks) {
                drawUtils.drawConnectors(hand, HandLandmarker.HAND_CONNECTIONS, {
                  color: '#FF9900CC', lineWidth: 3,
                });
                // Fingertip dots
                for (const idx of [4, 8, 12, 16, 20]) {
                  const pt = hand[idx];
                  ctx.beginPath();
                  ctx.arc(pt.x * canvas.width, pt.y * canvas.height, 5, 0, Math.PI * 2);
                  ctx.fillStyle = '#FF9900';
                  ctx.fill();
                  ctx.strokeStyle = '#fff';
                  ctx.lineWidth = 1.5;
                  ctx.stroke();
                }
              }
              ctx.restore();
            }

            // Reject hands whose center is inside the current face bbox —
            // these are nearly always MediaPipe Hands false positives where
            // the model latches onto facial features. The check is bypassed
            // when no face bbox is available.
            const faceBbox = faceBboxRef?.current;
            const filteredLandmarks = (result.landmarks ?? []).filter(hand => {
              if (!faceBbox) return true;
              const cx = (hand[0].x + hand[9].x) / 2;
              const cy = (hand[0].y + hand[9].y) / 2;
              const inside =
                cx >= faceBbox.minX && cx <= faceBbox.maxX &&
                cy >= faceBbox.minY && cy <= faceBbox.maxY;
              return !inside;
            });

            // Process primary hand
            if (filteredLandmarks.length > 0) {
              const primary = filteredLandmarks[0];
              // Hand center = average of wrist(0) + middle finger MCP(9)
              const cx = (primary[0].x + primary[9].x) / 2;
              const cy = (primary[0].y + primary[9].y) / 2;

              // Writing detection: speed of hand center movement
              let speed = 0;
              if (prevCenter) {
                const dx = cx - prevCenter.x;
                const dy = cy - prevCenter.y;
                speed = Math.sqrt(dx * dx + dy * dy);
              }
              prevCenter = { x: cx, y: cy };

              frameCount++;
              let fps = state.fps;
              if (now - fpsTimer > 1000) {
                fps = frameCount;
                frameCount = 0;
                fpsTimer = now;
              }

              const penGrip = detectPenGrip(primary);
              const phoneGripRaw = detectPhoneGrip(primary);
              // Mutual exclusion: pen-grip wins when both fire (tighter pinch).
              const phoneGrip: PhoneGripResult = penGrip.isGrip
                ? { isGrip: false, center: phoneGripRaw.center, confidence: 0 }
                : phoneGripRaw;
              gripSustain = phoneGrip.isGrip ? gripSustain + 1 : 0;
              penGripSustain = penGrip.isGrip ? penGripSustain + 1 : 0;
              const tremorMetric = tremor.update(primary, now, videoEl.videoWidth);

              // ── Secondary hand (양손 협응 시나리오) ──
              let secondary: HandGripSnapshot | null = null;
              let coordKind: HandCoordinationKind = null;
              if (filteredLandmarks.length >= 2) {
                const sec = filteredLandmarks[1];
                const scx = (sec[0].x + sec[9].x) / 2;
                const scy = (sec[0].y + sec[9].y) / 2;
                const secPen = detectPenGrip(sec);
                const secPhoneRaw = detectPhoneGrip(sec);
                const secPhone: PhoneGripResult = secPen.isGrip
                  ? { isGrip: false, center: secPhoneRaw.center, confidence: 0 }
                  : secPhoneRaw;
                secPhoneSustain = secPhone.isGrip ? secPhoneSustain + 1 : 0;
                secPenSustain = secPen.isGrip ? secPenSustain + 1 : 0;
                secondary = {
                  center: { x: scx, y: scy },
                  phoneGrip: secPhone,
                  penGrip: secPen,
                  phoneSustainFrames: secPhoneSustain,
                  penSustainFrames: secPenSustain,
                };

                // Coordination kind — both hands must show sustained grip (≥3f).
                const COORD_THRESH = 3;
                const priPen   = penGripSustain   >= COORD_THRESH;
                const priPhone = gripSustain      >= COORD_THRESH;
                const secPenOn   = secPenSustain   >= COORD_THRESH;
                const secPhoneOn = secPhoneSustain >= COORD_THRESH;
                if (priPen && secPhoneOn)       coordKind = 'pen+phone';
                else if (priPhone && secPenOn)  coordKind = 'pen+phone';
                else if (priPhone && secPhoneOn) coordKind = 'phone+phone';
                else if (priPen && secPenOn)    coordKind = 'pen+pen';
              } else {
                secPhoneSustain = 0;
                secPenSustain = 0;
              }
              coordSustain = coordKind ? coordSustain + 1 : 0;

              setState({
                detected: true,
                handCount: filteredLandmarks.length,
                center: { x: cx, y: cy },
                isWriting: speed > WRITING_SPEED_THRESH,
                landmarks: primary,
                fps,
                phoneGrip,
                penGrip,
                gripSustainFrames: gripSustain,
                penGripSustainFrames: penGripSustain,
                tremor: tremorMetric,
                secondary,
                coordinationKind: coordKind,
                coordinationSustainFrames: coordSustain,
              });
            } else {
              prevCenter = null;
              gripSustain = 0;
              penGripSustain = 0;
              secPhoneSustain = 0;
              secPenSustain = 0;
              coordSustain = 0;
              const tremorMetric = tremor.update(null, now, videoEl.videoWidth);
              setState(prev => ({
                ...prev,
                detected: false, handCount: 0, center: null,
                landmarks: null, isWriting: false,
                phoneGrip: { isGrip: false, center: null, confidence: 0 },
                penGrip:   { isGrip: false, center: null, confidence: 0 },
                gripSustainFrames: 0,
                penGripSustainFrames: 0,
                tremor: tremorMetric,
                secondary: null,
                coordinationKind: null,
                coordinationSustainFrames: 0,
              }));
            }
          } catch {
            // Ignore transient errors
          }
        };

        animRef.current = requestAnimationFrame(detect);
      } catch (err) {
        console.error('[useHandLandmarker] Init failed:', err);
      }
    };

    init();
    return () => { disposed = true; cancelAnimationFrame(animRef.current); };
  }, [enabled, videoElementId, overlayCanvasId]);

  return state;
}
